import { FunctionComponent, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";

import { ReactComponent as ChevronUp } from "@assets/icons/inbox/chevron-up.svg";

import { ReactComponent as ChevronDown } from "@assets/icons/inbox/chevron-down.svg";

import { useViewportClampedPanel } from "@/utils/useViewportClampedPanel";

import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import { ButtonSize } from "../Button/types";
import { SvgIcon } from "../Icon/SvgIcon";
import { Information } from "../Information/Information";

import { DropdownOption } from "./DropdownOption";

/**
 * Window event broadcast when any Dropdown opens. Every other Dropdown instance listens and
 * closes itself, so only one floating menu is ever open at a time. We coordinate via an event
 * (rather than letting the click bubble to siblings' outside-click handlers) because the trigger
 * calls `stopPropagation` to avoid firing clickable parents — so the bubble never reaches them.
 */
const DROPDOWN_OPEN_EVENT = "bb-dropdown-open";

/**
 * The item a controlled `value` names. Ids identify a row, titles are display text and repeat all
 * the time in real data — the staff ticket assignee roster holds 21 people called "Jonathan
 * Leupert" — so matching on the title can resolve to a *different* row than the caller asked for.
 * That is not cosmetic: the sync effect below reports what it resolved through `handleSelect`, so
 * the wrong row is handed back to the parent and written (PREC-4069 reassigned a ticket from user
 * 4160 to user 3390, a namesake at another company). Match on the id, and fall back to the title
 * only for a value whose id is not in `data` at all.
 */
const itemFor = (data: AutoCompleteItem[], value: AutoCompleteItem): AutoCompleteItem | undefined =>
	data.find((item) => item.id === value.id) ?? data.find((item) => item.title === value.title);

export interface AutoCompleteItem {
	id: number | string | null;
	title: string;
	icon?: JSX.Element;
	/** A heading rendered above this item in the menu (it starts a group); never selectable. */
	groupHeading?: string;
}

export interface DropdownProps {
	handleSelect: (value: AutoCompleteItem) => void;
	data: AutoCompleteItem[];
	label?: string;
	title?: string;
	testId?: string;
	floating?: boolean;
	value?: AutoCompleteItem;
	information?: string | JSX.Element;
	defaultValue?: AutoCompleteItem;
	disabled?: boolean;
	size?: ButtonSize;
	classNameDropdown?: string;
	classNameButton?: string;
	showTickIcon?: boolean;
	closeByClick?: boolean;
	error?: boolean;
	isOptional?: boolean;
	name?: string;
	resultText?: string;
	portal?: boolean;
	variant?: "default" | "pill";
	icon?: JSX.Element;
}

/**
 * Dropdown — single-select custom dropdown built on plain buttons (no MUI).
 *
 * Usage
 * -----
 * ```tsx
 * const options = useMemo(
 *   () => [{ id: "a", title: "Option A" }, { id: "b", title: "Option B" }],
 *   []
 * );
 * const selected = useMemo(() => options.find((o) => o.id === value), [options, value]);
 *
 * <Dropdown
 *   floating
 *   data={options}
 *   defaultValue={selected}
 *   handleSelect={(item) => setValue(item.id as string)}
 * />
 * ```
 *
 * Props of note
 * -------------
 * - `data` — the list of selectable items. Memoize it so the reference is stable across renders.
 * - `handleSelect` — called when the user picks an item. Receives the full `AutoCompleteItem`.
 * - `defaultValue` — preferred way to seed / drive the selected item. The internal state is
 *   resynced whenever the `defaultValue` reference changes, so as long as you derive it from
 *   a memoized `data` array (e.g. `data.find(...)`) it will reflect external changes cleanly.
 * - `value` — controlled selection. Avoid when possible: the internal sync effect also fires
 *   `handleSelect` and closes the menu, which will snap the dropdown shut if the parent
 *   re-renders with a fresh `handleSelect` identity (inline arrow functions). Prefer
 *   `defaultValue` for almost all cases.
 *   **Never pair `value` with a `handleSelect` that writes** (a mutation, a PATCH, a save): the
 *   effect fires it on every render, not on a user action, so a render becomes a write and each
 *   write's cache invalidation causes the next render. PREC-4069 was that loop — the staff ticket
 *   rail sent 9,414 PATCHes in 20 seconds, unassigned the ticket nobody had touched, and left
 *   every dropdown disabled (`isTriaging`) so the ticket could not be closed. A handler that
 *   writes belongs on a `defaultValue` dropdown, where only a real click reaches it.
 * - `floating` — renders the menu with `position: absolute` so it overlays surrounding
 *   layout instead of pushing content down. Enable this inside modals / form rows.
 * - `portal` — renders the menu into `document.body` via `ReactDOM.createPortal`. Use when
 *   the dropdown lives inside a scroll / overflow container that would clip the menu.
 * - `title` — static label prefix (e.g. "Sort: ") rendered next to the selected title.
 * - `closeByClick` — defaults to `true`. Set to `false` when selecting must not close the menu
 *   (e.g. multi-step pickers).
 * - `showTickIcon` — renders a tick next to the currently selected item in the menu.
 * - `variant="pill"` — renders the trigger as a compact, width-fitting meta pill
 *   (icon + label + small chevron) instead of the full-width input-style button.
 * - `icon` — optional leading icon for the pill trigger.
 *
 * Gotchas
 * -------
 * - Do not pass inline object literals as `value` or `defaultValue` (e.g.
 *   `value={{ id, title }}`). A new reference every render will retrigger the internal
 *   sync effect. Always derive from the memoized `data` array or memoize the item.
 * - `handleSelect` is captured inside a `useCallback` — if you want the closure to see
 *   fresh state, stabilize your handler with `useCallback` in the parent.
 * - **Re-picking the current item still fires `handleSelect`.** There is no same-value guard
 *   here, deliberately — some pickers want the re-select. So a handler that does more than
 *   `setState` must compare the incoming `id` against the current selection first: PREC-4151
 *   was a handler that paused the campaign builder's autosave for a hand-off that then did not
 *   happen, leaving it paused for the rest of the session with nothing on screen to say so.
 */
export const Dropdown: FunctionComponent<DropdownProps> = ({
	handleSelect,
	data,
	label,
	title,
	testId,
	information,
	floating,
	value,
	defaultValue,
	disabled,
	size,
	classNameDropdown,
	classNameButton,
	showTickIcon,
	closeByClick = true,
	error,
	isOptional,
	name,
	portal,
	resultText = "noResults",
	variant = "default",
	icon,
}) => {
	const { t } = useTranslation();
	const instanceId = useId();
	const [searchValue, setSearchValue] = useState<AutoCompleteItem>();
	const [selectedValue, setSelectedValue] = useState<AutoCompleteItem | undefined>(defaultValue);
	const [portalPosition, setPortalPosition] = useState<{
		top: number;
		left: number;
		width: number;
	} | null>(null);

	const handleSelectItem = useCallback(
		(selected: AutoCompleteItem) => {
			setSearchValue(selected);
			setSelectedValue(selected);
			handleSelect(selected);
			closeByClick && setShowDropdown(false);
		},

		[handleSelect]
	);

	useEffect(() => {
		if (defaultValue) {
			setSelectedValue(defaultValue);
		} else {
			setSelectedValue(undefined);
		}
	}, [defaultValue]);

	useEffect(() => {
		if (value) {
			const selected = itemFor(data, value);

			if (selected) {
				handleSelectItem(selected);
				setSelectedValue(selected);
			}
		}
	}, [data, handleSelectItem, value]);

	// Dropdown Logic
	const [showDropdown, setShowDropdown] = useState(false);
	const selectFieldRef = useRef<HTMLDivElement>(null);
	const dropdownMenuRef = useRef<HTMLDivElement>(null);

	// Keeps the portalled panel inside the viewport wherever the trigger sits (PREC-4138).
	useViewportClampedPanel(dropdownMenuRef, showDropdown);

	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			const insideTrigger =
				selectFieldRef.current && selectFieldRef.current.contains(target as Node);
			const insidePortalMenu =
				dropdownMenuRef.current && dropdownMenuRef.current.contains(target as Node);

			if (!insideTrigger && !insidePortalMenu) {
				if (showDropdown) {
					setShowDropdown(false);
				}
			}
		};

		window.addEventListener("click", handleClick);

		return () => window.removeEventListener("click", handleClick);
	}, [selectFieldRef, showDropdown]);

	// Close this menu whenever another Dropdown opens, so only one is ever open at a time.
	useEffect(() => {
		const handleOtherOpen = (event: Event) => {
			if ((event as CustomEvent<string>).detail !== instanceId) {
				setShowDropdown(false);
			}
		};

		window.addEventListener(DROPDOWN_OPEN_EVENT, handleOtherOpen);

		return () => window.removeEventListener(DROPDOWN_OPEN_EVENT, handleOtherOpen);
	}, [instanceId]);

	useEffect(() => {
		if (!portal || !showDropdown) {
			return;
		}

		const updatePosition = () => {
			if (selectFieldRef.current) {
				const rect = selectFieldRef.current.getBoundingClientRect();

				setPortalPosition({
					top: rect.bottom,
					left: rect.left,
					width: rect.width,
				});
			}
		};

		updatePosition();

		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [portal, showDropdown]);

	// Result rendering
	const renderResults = () => {
		const results = data;

		if (!data.length) {
			return (
				<p className="py-2.5 px-3 text-sm italic text-gray-700">{t(`basics.${resultText}`)}</p>
			);
		}

		return (
			<div className={results.length ? "border-b border-b-border items-start" : ""}>
				{results.map((item) => (
					<DropdownOption
						key={`${name ? `${name}-` : ""}option-select-${item.id}`}
						item={item}
						ticked={Boolean(showTickIcon) && item.id === selectedValue?.id}
						onSelect={handleSelectItem}
					/>
				))}
			</div>
		);
	};

	const handleClickInput = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		const next = !showDropdown;

		setShowDropdown(next);

		// Announce that we're opening so any other open Dropdown closes itself.
		if (next) {
			window.dispatchEvent(new CustomEvent(DROPDOWN_OPEN_EVENT, { detail: instanceId }));
		}
	};

	const buttonSize = useMemo(() => {
		if (size === ButtonSize.XS) {
			return "py-1 !h-[32px] !rounded-[10px] text-xs font-semibold";
		}

		if (size === ButtonSize.S) {
			return "py-2 !h-[38px]";
		}

		return "py-[10px]";
	}, [size]);

	return (
		<div
			ref={selectFieldRef}
			className={classNames("relative ", variant === "pill" ? "w-fit" : "w-full")}
		>
			{label && (
				<label
					className={classNames(
						"block mb-2 flex flex-row text-sm items-center font-medium text-gray-900 appearance-none"
					)}
				>
					{label}
					{isOptional && " (Optional)"}
					{information && (
						<span className="ml-1">
							<Information information={information} />
						</span>
					)}
				</label>
			)}

			<button
				className={classNames(
					"relative",
					variant === "pill" ? "w-fit" : "w-full ",
					disabled ? "opacity-50" : "cursor-pointer"
				)}
				data-test={testId}
				type="button"
				onClick={!disabled ? handleClickInput : undefined}
			>
				{variant === "pill" ? (
					<div
						className={classNames(
							"inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-lg border border-border-light bg-brand-white px-2 py-[5px] text-xs font-medium text-text-meta hover:bg-background-light duration-200",
							classNameButton,
							error ? "border !border-status-error" : ""
						)}
						data-test="search-input"
					>
						{icon && <span className="flex items-center text-gray-700">{icon}</span>}
						<span className="whitespace-nowrap">
							{title
								? `${t(`${title}`)}${selectedValue?.title ? `: ${selectedValue.title}` : ""}`
								: searchValue?.title || selectedValue?.title || t("basics.pleaseSelect")}
						</span>
						<SvgIcon
							className="w-[14px] h-[14px] text-text-disabled pointer-events-none"
							svgIcon={showDropdown ? ChevronUp : ChevronDown}
						/>
					</div>
				) : title ? (
					<div
						className={classNames(
							"input-styles pr-12",
							"flex flex-row items-center",
							"pl-3 hover:shadow-sm ",
							buttonSize,
							classNameButton,
							error ? "border !border-status-error" : ""
						)}
						data-test="search-input"
					>
						<div className="flex flex-shrink-0 whitespace-nowrap">
							<div className="flex-shrink-0">{t(`${title}`)}</div>
							<div className="flex-shrink-0">
								{selectedValue?.title && `: ${selectedValue?.title}`}
							</div>
						</div>
						<div
							className={classNames(
								"absolute right-[10px]  translate-y-[-50%] peer-focus:text-brand-light",
								"top-[50%]"
							)}
						>
							<SvgIcon
								className="w-[20px] h-[20px] pointer-events-none"
								svgIcon={showDropdown ? ChevronUp : ChevronDown}
							/>
						</div>
					</div>
				) : (
					<div
						className={classNames(
							"input-styles flex flex-row pr-8 peer",
							"pl-3 hover:shadow-sm ",
							buttonSize,
							classNameButton,
							error ? "border !border-status-error" : "",
							searchValue?.title || selectedValue?.title ? "text-gray-900" : "text-gray-500"
						)}
						data-test="search-input"
					>
						<div className="flex items-center gap-2 overflow-hidden text-ellipsis whitespace-nowrap w-full text-left leading-5">
							{searchValue?.icon && <span className="flex items-center">{searchValue.icon}</span>}
							{searchValue?.title || selectedValue?.title || t("basics.pleaseSelect")}
						</div>
						<div
							className={classNames(
								"absolute right-[10px]  translate-y-[-50%] transform text-gray-500 peer-focus:text-brand-light",
								"top-[50%]"
							)}
						>
							<SvgIcon
								className="w-[20px] h-[20px] pointer-events-none"
								svgIcon={showDropdown ? ChevronUp : ChevronDown}
							/>
						</div>
					</div>
				)}
			</button>

			{showDropdown &&
				(portal ? (
					ReactDOM.createPortal(
						<div
							ref={dropdownMenuRef}
							className={classNames(
								classNameDropdown ? classNameDropdown : "",
								"fixed z-[2000] mt-1 overflow-hidden bg-white border border-gray-200 rounded-md max-h-[320px] overflow-y-auto bb-scrollbar-darker"
							)}
							style={
								portalPosition
									? {
											top: portalPosition.top,
											left: portalPosition.left,
											minWidth: portalPosition.width,
										}
									: { visibility: "hidden" }
							}
							onClick={(e) => e.stopPropagation()}
						>
							{renderResults()}
						</div>,
						document.body
					)
				) : (
					<div
						className={classNames(
							classNameDropdown
								? classNameDropdown
								: variant === "pill"
									? "w-max min-w-full"
									: "w-full",
							"z-[2000] mt-1 overflow-hidden bg-white border border-gray-200 rounded-md max-h-[320px] overflow-y-auto bb-scrollbar-darker",
							floating ? "absolute" : "relative"
						)}
					>
						{renderResults()}
					</div>
				))}
		</div>
	);
};
